import { Box } from '@mui/material';
import { useEffect } from 'react';
import { App, Maybe, NavigationLink, Page } from '@/types/generated';
import type { IBanner } from '@shared-components/components/Banner/Banner';
import type { IGoogleMap } from '@shared-components/components/GoogleMap/GoogleMap';
import { getHeaderWrapperStyles } from '@shared-components/components/Header/Header.styles';
import HeaderDesktop from '@shared-components/components/Header/HeaderDesktop/HeaderDesktop';
import HeaderMobile from '@shared-components/components/Header/HeaderMobile/HeaderMobile';
import { useAppContext, useFacilityContext } from '@shared-components/context';
export interface IHeader {
  /**
   * Application data, containg all the information about the application
   *
   * @type {App}
   * @memberof IHeader
   */
  appData: App;
  /**
   * Determines if the location cards will be used in the header or not
   *
   * @type {boolean}
   * @memberof IHeader
   */
  useHeaderLocationCards: boolean | null | undefined;
  /**
   * Whether to show (and fetch) the office details in the header or not.
   *
   * @type {boolean}
   * @memberof IHeader
   */
  withOfficeDetails?: boolean;
  /**
   * Whether to show the ODP links block in the header or not.
   *
   * @type {boolean}
   * @memberof IHeader
   */
  inlineNavigation?: boolean;
  topLinks?: boolean;
  logoPosition?: 'left' | 'center';
  navigationBar?: boolean;
  isFullScreenMenu?: boolean;
  withMenuCTA?: boolean;
  logo?: {
    dark: string;
    light: string;
  };
  hidePrimaryActionButton?: boolean;
  hideLocation?: boolean;
  showHeaderNavMenu?: boolean;
  logoHref?: string;
  locationNavigationLinks?: Array<NavigationLink | Page>;
  phoneNumber?: Maybe<NavigationLink>;
  showLanguageToggle?: boolean;
  /**
   * Fixed Header
   *
   * when it is true, header won't disappear when scrolling down
   * NOTE:
   * Sticky header and Fixed header in most cases are the same.
   * but sticky header is the one that disappears when scrolling down
   * and reappears when scrolling up in our case today
   *
   * Default is false
   *
   * @type {boolean}
   * @memberof IHeader
   */
  withFixedHeader?: boolean;
  /**
   * Hide hamburger menu button on mobile.
   *
   * @type {boolean}
   * @memberof IHeader
   */
  hideHamburgerMenuToggleButton?: boolean;
  /**
   * GoogleMap data for Header
   *
   * @type {string}
   * @memberof IHeader
   */
  googleMapsForLocationCards?: IGoogleMap;
  /**
   * Top Alert Banner for Header
   *
   * @type {ContentTypeRichText[]}
   * @memberof IHeader
   */
  topAlertBanner?: IBanner;
  /**
   * Bottom Alert Banner for Header
   *
   * @type {ContentTypeRichText[]}
   * @memberof IHeader
   */
  bottomAlertBanner?: IBanner;
  /**
   * Disable Top Alert Banner for Header
   *
   * @type {boolean}
   * @memberof IHeader
   */
  disableHeaderTopAlertBanner?: boolean | null;
  /**
   * Disable Bottom Alert Banner for Header
   *
   * @type {boolean}
   * @memberof IHeader
   */
  disableHeaderBottomAlertBanner?: boolean | null;
}
export type TAppDataConfig = {
  enableGroupHeaderMenu?: boolean;
};
export default function Header({
  appData,
  logo,
  logoHref = '/',
  topAlertBanner,
  bottomAlertBanner,
  disableHeaderTopAlertBanner = false,
  disableHeaderBottomAlertBanner = false
}: Readonly<IHeader>) {
  const logoUrl = appData?.header?.darkHeader ? logo?.light : logo?.dark;
  const {
    officeInfo
  } = useFacilityContext();
  const {
    config
  } = useAppContext();
  const {
    enableGroupHeaderMenu
  } = appData?.config as TAppDataConfig || {};
  const queryParams = globalThis?.location?.search ? new URLSearchParams(globalThis.location.search) : null;
  const queryDisplayHeaderGroupMenu = queryParams?.get('displayHeaderGroupMenu');
  const hasNavigationLinks = (appData?.header?.navigationLinksCollection?.items?.length ?? 0) > 0;

  // Persist the test flag so it survives client-side navigation to routes (e.g. /locations) without the query param.
  useEffect(() => {
    if (queryDisplayHeaderGroupMenu !== null && queryDisplayHeaderGroupMenu !== undefined) {
      globalThis?.sessionStorage?.setItem('displayHeaderGroupMenu', queryDisplayHeaderGroupMenu);
    }
  }, [queryDisplayHeaderGroupMenu]);
  const persistedDisplayHeaderGroupMenu = globalThis?.sessionStorage?.getItem('displayHeaderGroupMenu');
  const displayHeaderGroupMenu: boolean = (enableGroupHeaderMenu || queryDisplayHeaderGroupMenu === 'true' || persistedDisplayHeaderGroupMenu === 'true') && hasNavigationLinks;
  const showMobileHeaderLocationBar = config?.featureFlags?.showMobileHeaderLocationBar;
  const showTopAlertBannerMessagesDesktop = !disableHeaderTopAlertBanner && (topAlertBanner?.alertMessagesCollection?.items ?? []).length > 0;
  const showTopAlertBannerMessagesMobile = !disableHeaderTopAlertBanner && (topAlertBanner?.mobileAlertMessagesCollection?.items ?? []).length > 0;
  const showBottomAlertBannerMessagesDesktop = !disableHeaderBottomAlertBanner && (bottomAlertBanner?.alertMessagesCollection?.items ?? []).length > 0;
  const showBottomAlertBannerMessagesMobile = !disableHeaderBottomAlertBanner && (bottomAlertBanner?.mobileAlertMessagesCollection?.items ?? []).length > 0;
  useEffect(() => {
    if (officeInfo?.code) {
      void import('@utils/dataLayerConfig').then(({
        dataLayerConfig
      }) => {
        dataLayerConfig({
          event: 'facility_set',
          brand: config?.name ?? '',
          website_selected_facility_id: officeInfo?.code,
          website_selected_facility_name: officeInfo?.name
        });
        return true;
      }).catch(() => {
        // Best-effort analytics event; avoid noisy console errors on lazy-load failures.
      });
    }
  }, [officeInfo?.code, config?.name, officeInfo?.name]);
  return <Box id="site-header" // allows other components find header by ID
  data-testid="section_header" sx={getHeaderWrapperStyles(showMobileHeaderLocationBar, showTopAlertBannerMessagesMobile, showBottomAlertBannerMessagesMobile, showTopAlertBannerMessagesDesktop, showBottomAlertBannerMessagesDesktop)} data-sentry-element="Box" data-sentry-component="Header" data-sentry-source-file="Header.tsx">
			<Box sx={{
      display: {
        xs: 'none',
        md: 'block'
      }
    }} data-sentry-element="Box" data-sentry-source-file="Header.tsx">
				<HeaderDesktop displayHeaderGroupMenu={displayHeaderGroupMenu} headerData={appData.header} logoUrl={logoUrl || appData.logo?.url} logoHref={logoHref} data-sentry-element="HeaderDesktop" data-sentry-source-file="Header.tsx" />
			</Box>
			<Box sx={{
      display: {
        xs: 'block',
        md: 'none'
      }
    }} data-sentry-element="Box" data-sentry-source-file="Header.tsx">
				<HeaderMobile displayHeaderGroupMenu={displayHeaderGroupMenu} headerData={appData.header} logoUrl={appData.logoMobile?.url || appData.logo?.url} logoHref={logoHref} data-sentry-element="HeaderMobile" data-sentry-source-file="Header.tsx" />
			</Box>
		</Box>;
}